import { useCallback, useEffect, useState } from 'react';

/**
 * 설정의 **광고 On/Off** (v21, `docs/45` §9.8 · D25).
 *
 * ★ **데모 장치이지 제품 기능이 아니다.** 켠 채와 끈 채를 나란히 보이기 위한 것이고,
 * 그래서 **서버에도 계정에도 저장하지 않는다** — `localStorage` 한 키다.
 *
 * **채널을 가리지 않는다.** 채널마다 두면 「이 채널만 광고가 나온다」가 되고, 그것은
 * 정책(`adPolicy`)이 이미 말하는 것이다. 이 토글이 말하는 것은 **지금 이 무대에서 광고를
 * 보일 것인가** 하나다.
 *
 * ★ **없으면 켜짐이다.** 정책이 진실이고 이 토글은 **억제**다 — 저장소가 막혀 있다고
 * (사파리 프라이빗) 광고가 통째로 사라지면, 데모 무대에서 그 사실을 아무도 모른 채
 * 「광고가 안 나온다」를 서버에서 찾게 된다.
 */
const STORAGE_KEY = 'bloom.adsEnabled';

/** 같은 문서 안의 다른 컴포넌트에 알린다. `storage` 이벤트는 **다른 탭에만** 온다 */
const CHANGE_EVENT = 'bloom:ads-enabled';

export function adsEnabled(): boolean {
  try {
    // **`'false'` 만 끈 것이다.** 값이 없거나 이상하면 켜짐이다(위 ★).
    return localStorage.getItem(STORAGE_KEY) !== 'false';
  } catch {
    return true;
  }
}

export function setAdsEnabled(next: boolean): void {
  try {
    localStorage.setItem(STORAGE_KEY, next ? 'true' : 'false');
  } catch {
    // 저장하지 못해도 이 탭에서는 아래 이벤트로 바뀐다. 새로고침하면 켜짐으로 돌아간다.
  }
  window.dispatchEvent(new CustomEvent(CHANGE_EVENT));
}

/**
 * 화면이 이 값을 그린다. **재생 경로는 이것을 구독하지 않는다** — 어댑터가 `requestAds`
 * 를 부르는 **그 순간** `adsEnabled()` 를 읽는다(`create-ad-adapter.ts`).
 *
 * 그래서 토글이 **리시버를 다시 세우지 않는다** — 영상이 처음부터 다시 시작하지 않는
 * 것이 이 구조의 요점이다 (절대 규칙 25·51).
 */
export function useAdsEnabled(): [boolean, (next: boolean) => void] {
  const [enabled, setEnabled] = useState(adsEnabled);

  useEffect(() => {
    const sync = () => {
      setEnabled(adsEnabled());
    };
    window.addEventListener(CHANGE_EVENT, sync);
    // 다른 탭에서 바꾼 경우.
    window.addEventListener('storage', sync);
    return () => {
      window.removeEventListener(CHANGE_EVENT, sync);
      window.removeEventListener('storage', sync);
    };
  }, []);

  const set = useCallback((next: boolean) => {
    setAdsEnabled(next);
    setEnabled(next);
  }, []);

  return [enabled, set];
}
